import EngineCleanupBase from '../../services/engines/EngineCleanupBase';
import componentNotifier from '../../services/wss/ComponentNotifier';
import { EngineNames } from '../../services/wss/events/EngineNames';
import umlNotesCache from './UmlNotesCache';
import umlNoteFetcher from './UmlNoteFetcher';

const _name = EngineNames.UmlNotesEngine;

/**
 * UmlSchemaListEngine  holds
 *
 */
class UmlNotesEngine extends EngineCleanupBase {
    /**
     * initialize engine + eenmalige fetch. Aangeroepen vanuit PageContent.componentDidMount.
     */
    bootup = () => {
        this.initialize(_name);
    };

    /**
     * Fetch alle UmlSchema-notes van de BE (1x, zie class-doc). Consumers moeten dit
     * zelf NIET aanroepen — lees via getAll()/getByRk().
     */
    fetchAll = async () => {
        await umlNoteFetcher.fetch();
        this._notify();
    };

    /**
     * Alle gecachete UmlSchema-notes — gebruikt door LoadUmlSchemaDlg voor de lijst.
     * @returns {object[]}
     */
    getAll = () => umlNotesCache.getAll();

    /**
     * Eén UmlSchema-note by rk, of null. Gebruikt door UmlSchemaDlg.handleLoadSelected
     * en UmlSchemaFetcher.loadSchema — i.p.v. een verse fetch bij elke lookup.
     * @param {string} rk
     * @returns {object|null}
     */
    getByRk = (rk) => umlNotesCache.get(rk);

    /**
     * Voegt een net opgeslagen/aangemaakte note direct toe aan de cache — aangeroepen
     * vanuit UmlSchemaFetcher.saveSchema() na een succesvolle save, zodat de Load Uml-lijst
     * meteen klopt zonder te wachten op de SignalR OnJournalCreated/OnJournalUpdated round-trip.
     * @param {object} note
     */
    upsert = (note) => {
        try {
            if (!note?.rk) return;
            umlNotesCache.addOrUpdate([note]);
            this._notify();
        } catch (error) {
            this.logError('upsert', error.message);
        }
    };

    /**
     * Update entries in cache — gerouteerd vanuit PageContentSRHandlers.js
     * (OnJournalCreated/OnJournalUpdated/OnEntryNodesResolved).
     * @param {object[]} entries
     */
    onJournalUpdated = (entries) => {
        try {
            if (!Array.isArray(entries) || !entries.length) return false;

            const items = entries.filter((x) => x.type === 'UmlSchema');
            if (items.length === 0) return;

            umlNotesCache.addOrUpdate(items);
            this._notify();
            return true;
        } catch (error) {
            this.logError('onJournalUpdated', error.message);
            return false;
        }
    };

    /**
     * Verwijdert entry uit cache — gerouteerd vanuit PageContentSRHandlers.js (OnJournalDeleted).
     * @param {string} rk
     */
    onJournalDeleted = (rk) => {
        try {
            if (!rk) {
                this.logError('onJournalDeleted', `Invalid row key - ${rk}`);
                return false;
            }

            const deleted = umlNotesCache.delete(rk);
            if (!deleted) return;

            this._notify();
            return deleted;
        } catch (error) {
            this.logError('onJournalDeleted', error.message);
            return false;
        }
    };

    // ─── private ──────────────────────────────────────────

    _notify = () => {
        componentNotifier.notify(_name, 'dataChanged', {
            items: umlNotesCache.getAll(),
        });
    };

    clear = () => {
        umlNotesCache.clear();
        super.clear();
    };
}

const umlNotesEngine = new UmlNotesEngine();
export default umlNotesEngine;
